import { useMemo } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useAccount } from '../../context/AccountContext';
import {
  PUBLISH_TINYCMS_CONTENT,
  UNPUBLISH_TINYCMS_CONTENT,
  UPDATE_PROJECT,
  UPDATE_TINYCMS_ARTICLE,
} from '../../graphql';
import { showInkToast } from '../cms/InkToast';
import {
  groupMissed,
  missedGroupWords,
  missedNote,
  retryLabel,
  staleReason,
} from '../cms/missedPost';
import { useAgentConnected } from '../cms/AgentStatus';
import {
  TodayAgentRequest,
  TodayCampaign,
  TodayDataset,
  TodayGroup,
  TodayWaiting,
  useToday,
} from '../cms/todayData';
import { groupWords } from '../cms/todayGroups';
import { bundleItems, usePublishTogether } from '../cms/publishTogether';
import {
  APPROVE_CAMPAIGN,
  APPROVE_SOCIAL_POST,
  calendarDate,
  campaignRange,
  campaignStart,
  channelMark,
  channelName,
  postText,
  postTime,
  ReachPost,
  REACH_CHANNELS,
  REQUEST_SOCIAL_POST_CHANGES,
  RESTORE_MISSED_SOCIAL_POST,
  RETRY_SOCIAL_POST,
  needsReconnect,
  shortDate,
  SKIP_SOCIAL_POST,
  toDate,
} from '../reach/reach';
import { useAddons } from '../cms/addons';
import {
  DECIDE_SUPPORT_ACCESS,
  endsOn,
  supportName,
  useSupportAccess,
} from '../settings/supportAccess';
import {
  ago,
  CHIP,
  checkLine,
  CheckLine,
  CheckTone,
  copyEditLine,
  DAY,
  EFFECT,
  fixLabel,
  missedEffect,
  NOTHING_TO_CHECK,
  plural,
  postsEffect,
  proposerLine,
  reviewHref,
  staleEffect,
  time,
  when,
} from './approvalWords';
import {
  DECIDE_AGENT_ACTION,
  REOPEN_AGENT_ACTION,
  REQUEST_TINYCMS_CHANGES,
  RESTORE_WAITING_SOCIAL_POST,
  TINYCMS_STARTER_REVIEW,
  WITHDRAW_TINYCMS_CHANGE_REQUEST,
} from './approvalsGql';
import {
  filledCount,
  filledRows,
  newSiteEffect,
  NewSiteRow,
  placeholderCount,
  placeholderNote,
  placeholderRows,
  reviewHash,
  SITE_IDENTITY,
  StarterReview,
} from './newSiteWords';
import { useTinycmsSites } from '../cms/sites';
import { orderQueue } from './approvalOrder';
import { useVoiceRewrite } from '../reach/useVoiceRewrite';
import { cardVoice, itemWarning, offVoiceLine, VoiceRow, VoiceSubject } from '../reach/voiceWords';
import { POST_STATUS } from '../statusWords';

/**
 * Everything waiting on the person, one item each, for the Approvals review
 * (design 2026-10-27). Built from what Today's cards were (moved here when
 * the APPROVALS section left Today): site content and the bundles that go
 * live together, Reach posts that are waiting or didn't go out, campaigns,
 * and — never shown before (backlog 44ed2773) — agent Ask first requests,
 * datasets waiting on a yes, and technical support access requests (owner
 * only). Each item says what approving does, carries its own approve, Ask
 * for changes (with Undo) and editor link, and what its preview renders.
 */

/** One post as the preview shows it. */
export interface PostView {
  id: string;
  mark: string;
  network: string;
  meta: string;
  text: string;
  image: string | null;
  url: string | null;
  warn: string[];
}

export type PreviewSpec =
  | { type: 'article'; articleId: string; scheduled?: boolean }
  | {
      type: 'site';
      /** The page whose working copy renders (none: the site's home). */
      pageId: string | null;
      /** A new site from a template: outline what was filled in and the placeholders (#filled=…). */
      review?: string;
      /** What changed, in plain words (the site document's change labels or the members). */
      changes: string[];
      changesTitle: string;
      where: string;
    }
  | { type: 'posts'; posts: PostView[]; failed?: boolean }
  | { type: 'dataset'; articleId: string; name: string | null; records: number | null }
  | { type: 'request'; label: string; quote: string; lines?: string[]; where: string }
  | { type: 'note'; text: string; lines?: string[]; where: string };

export interface ApprovalItem {
  key: string;
  /** "kind:id" keys an approval link selects it by (/approvals/:kind/:id). */
  match: string[];
  tag: string;
  failed?: boolean;
  who: string;
  ago: string;
  /** For the queue's order: oldest first within its group (approvalOrder). */
  at: number;
  title: string;
  note?: string | null;
  check: CheckLine;
  /** Voice and style findings under the check line, and the one rewrite for the card. */
  voice?: { rows: VoiceRow[]; rewrite: () => Promise<unknown> };
  effect: string;
  /** The primary action's words: "Approve", "Approve campaign", "Retry" … */
  cta: string;
  approve: () => Promise<void>;
  /** An open check leads (design §7): its fix (opens the editor) is the primary action. */
  fix?: { label: string; to: string };
  /** The approval still leads; the fix is the secondary button (a new site's warnings). */
  fixSecondary?: boolean;
  blocked?: boolean;
  /** Held, and its fix goes to the agent. */
  held?: boolean;
  /** Held: only owners and admins get Approve anyway (the server enforces this too). */
  heldForOwners?: boolean;
  /** What the agent is asked, when the card's title and check don't say which item. */
  heldFix?: string;
  /** Approve over a blocking check, with the reason recorded beside the approval. */
  approveAnyway?: (reason: string) => Promise<void>;
  /**
   * A page the account wants copy-edited that hasn't been: approving runs the
   * copy edit first, and Skip copy edit approves without it (approveAnyway).
   */
  copyEditPending?: boolean;
  /** Skip, Dismiss, Decline: in place of Ask for changes where that means nothing. */
  secondary?: { label: string; run: () => Promise<void> };
  /** Skip for a single post whose time passed: beside the usual actions. */
  extra?: { label: string; run: () => Promise<void> };
  /**
   * An article whose lead check is its main image's missing alt text: the fix
   * is a field in the queue, not a trip to the editor (feedback 25c2adc6).
   */
  heroAlt?: (alt: string) => Promise<void>;
  /** Ask for changes: sends the note (may be empty); resolves to the Undo. */
  ask?: (note: string) => Promise<() => Promise<void>>;
  /** An ended campaign: Reschedule shows this prompt for the agent. */
  prompt?: string;
  /** Technical support access: approve for a span of days. */
  approveFor?: (days: number) => Promise<void>;
  /** A missed post that's old news: write a fresh one instead. */
  fresh?: ReachPost;
  editorTo: string;
  preview: PreviewSpec;
  /** Shown with the approvals but left out of the count (a draft campaign starting soon). */
  notApproval?: boolean;
  /** A new site from a template (design 2026-10-27): what went in for the person, and what's left. */
  newSite?: {
    filled: NewSiteRow[];
    filledCount: string;
    placeholders: NewSiteRow[];
    placeholderCount: string;
    placeholderNote: string;
  };
}

export function useApprovalQueue({ skip = false } = {}) {
  const { accountId, selectedAccount } = useAccount();
  // The one place that polls: the top bar's count stays current while the app is open.
  const today = useToday('day', { skip, poll: !skip });
  const { data, waitingPosts, missedPosts, campaigns, allCampaigns, refetch } = today;
  const { reachOn } = useAddons();
  const agentConnected = useAgentConnected();
  const support = useSupportAccess(skip ? null : accountId, { poll: !skip });
  const [publish] = useMutation(PUBLISH_TINYCMS_CONTENT);
  const { publishTogether } = usePublishTogether();
  const [unpublish] = useMutation(UNPUBLISH_TINYCMS_CONTENT);
  const [approvePost] = useMutation(APPROVE_SOCIAL_POST);
  const [retryPost] = useMutation(RETRY_SOCIAL_POST);
  const [skipPost] = useMutation(SKIP_SOCIAL_POST);
  const [updateArticle] = useMutation(UPDATE_TINYCMS_ARTICLE);
  const [restoreMissed] = useMutation(RESTORE_MISSED_SOCIAL_POST);
  const [approveCampaign] = useMutation(APPROVE_CAMPAIGN);
  const { rewrite: rewriteInVoice } = useVoiceRewrite();
  const [updateProject] = useMutation(UPDATE_PROJECT);
  const [requestChanges] = useMutation(REQUEST_TINYCMS_CHANGES);
  const [withdrawChanges] = useMutation(WITHDRAW_TINYCMS_CHANGE_REQUEST);
  const [requestPostChanges] = useMutation(REQUEST_SOCIAL_POST_CHANGES);
  const [restoreWaiting] = useMutation(RESTORE_WAITING_SOCIAL_POST);
  const [decideAgent] = useMutation(DECIDE_AGENT_ACTION);
  const [reopenAgent] = useMutation(REOPEN_AGENT_ACTION);
  const [decideSupport] = useMutation(DECIDE_SUPPORT_ACCESS);
  // A site that isn't live yet may be a new one from a template: its card says what went in.
  const newSiteWaiting = Boolean(data?.waiting?.some((w) => w.kind === 'site' && !w.live));
  const starterQ = useQuery<{ tinycmsStarterReview: StarterReview | null }>(
    TINYCMS_STARTER_REVIEW,
    {
      variables: { accountId },
      skip: skip || !accountId || !newSiteWaiting,
      fetchPolicy: 'cache-and-network',
    }
  );
  const starterReview = newSiteWaiting ? starterQ.data?.tinycmsStarterReview || null : null;
  const { sites } = useTinycmsSites(skip);
  const siteHost = sites.find((s) => s.accountId === accountId)?.host || null;

  // Account names for the post cards, read once rather than per post.
  const channelsQ = useQuery<{
    socialProfiles: {
      id: string;
      platform: string;
      profileName?: string | null;
      profileUsername?: string | null;
    }[];
  }>(REACH_CHANNELS, { variables: { accountId }, skip: skip || !accountId || !reachOn });
  const accountNames = useMemo(
    () =>
      new Map(
        (channelsQ.data?.socialProfiles || []).map((c) => [
          c.id,
          c.profileName || (c.profileUsername ? `@${c.profileUsername.replace(/^@/, '')}` : ''),
        ])
      ),
    [channelsQ.data]
  );
  const contentLists = ['GetTinycmsArticles', 'GetTinycmsPages', 'GetTinycmsForms'];
  const siteName = selectedAccount?.name || 'Your site';
  const proposer = agentConnected ? 'proposed by your agent' : 'waiting for your approval';

  const items: ApprovalItem[] = useMemo(() => {
    if (!accountId) return [];
    const viewOf = (p: ReachPost): PostView => {
      const t = postTime(p);
      const network = channelName(p.platform);
      const account = accountNames.get(p.socialProfileId) || network;
      const stale = staleReason(postText(p), t);
      return {
        id: p.id,
        mark: channelMark(p.platform),
        network,
        meta: t ? `${account} · ${when(t)}` : account,
        text: postText(p),
        image: p.imageUrl || null,
        url: p.postUrl || null,
        warn: [
          ...(stale ? [stale] : []),
          ...(p.offSiteDomain ? [`Links to ${p.offSiteDomain}, not your site`] : []),
          ...(p.voiceCheck?.offVoice ? [`● ${offVoiceLine(p.voiceCheck)}`] : []),
          ...(itemWarning(p.itemCheck) ? [`● ${itemWarning(p.itemCheck)}`] : []),
        ],
      };
    };
    const waitingList = data?.waiting || [];
    const byId = new Map(waitingList.map((w) => [w.id, w]));
    const groupsById = new Map((data?.groups || []).map((g) => [g.id, g]));
    const shown = new Set<string>();

    // The toast's Undo takes back what this approval first put on the site; an
    // update to something already live has no "before" to go back to here.
    const undoFirstPublish = (list: { kind: string; id: string; live: boolean }[]) => {
      const fresh = list.filter((i) => !i.live && i.kind !== 'site');
      if (!fresh.length) return undefined;
      return {
        label: 'Undo',
        onClick: () =>
          void Promise.all(
            fresh.map((i) =>
              unpublish({
                variables: { accountId, kind: i.kind, id: i.id, reason: 'Undone from Approvals' },
                refetchQueries: contentLists,
              })
            )
          )
            .then(() => refetch())
            .catch((err) =>
              showInkToast(err instanceof Error ? err.message : 'Couldn’t undo that.')
            ),
      };
    };

    // Ask for changes on site content: one request per item shown, each on its version.
    const askContent =
      (list: { kind: string; id: string; workingHash: string }[]) => async (note: string) => {
        const ids: string[] = [];
        for (const i of list) {
          const { data: res } = await requestChanges({
            variables: { accountId, kind: i.kind, id: i.id, workingHash: i.workingHash, note },
          });
          if (res?.requestTinycmsChanges?.id) ids.push(res.requestTinycmsChanges.id);
        }
        return async () => {
          await Promise.all(ids.map((id) => withdrawChanges({ variables: { accountId, id } })));
        };
      };

    const contentFix = (check: CheckLine | null, to: string | undefined, fallback: string) =>
      check && check.tone !== 'ok'
        ? { label: fixLabel(check.text), to: to || fallback }
        : undefined;

    // Items that can only go live together — a page and the forms it places,
    // pages that link to each other, and the site's menus when they point at
    // them (a new site from a template): one item, approved together.
    const groupItem = (g: TodayGroup): ApprovalItem => {
      const members = (ids: string[]) =>
        ids.map((id) => byId.get(id)).filter((w): w is TodayWaiting => Boolean(w));
      const pages = members(g.pages);
      const forms = members(g.forms);
      const site = g.site ? waitingList.find((w) => w.kind === 'site') : undefined;
      const asMember = (w: TodayWaiting) => ({ ...w, kind: w.kind as 'page' | 'form' });
      const words = groupWords(pages.map(asMember), forms.map(asMember), {
        byAgent: agentConnected === true,
        site: site ? { live: site.live } : null,
      });
      const first = pages[0];
      const all = [...pages, ...forms, ...(site ? [site] : [])];
      const latest = all
        .map((w) => w.updatedAt || '')
        .sort()
        .pop();
      const copyEditPending = pages.some((w) => w.copyEditPending);
      const check = copyEditLine(checkLine(g.checks) || NOTHING_TO_CHECK, copyEditPending);
      const editorTo = reviewHref('page', first.id);
      // A new site from a template: what went in for the person and the placeholders left.
      const review = site && !site.live ? starterReview : null;
      const filled = review ? filledRows(review) : [];
      // Published without a share image: the toast says it's still to do.
      const done =
        review && !review.shareImage
          ? `${words.done} Add a share image in Website › Manage › Site when you can.`
          : words.done;
      const placeholders = review ? placeholderRows(review) : [];
      return {
        key: `group:${g.id}`,
        match: all.map((w) => `${w.kind}:${w.id}`),
        tag: words.chip,
        who: review ? `from the ${review.template.name} template` : proposer,
        ago: ago(latest),
        at: toDate(latest)?.getTime() || 0,
        title: words.title,
        note: words.note,
        check,
        effect: review
          ? newSiteEffect(pages.length, siteHost || review.filled.url || siteName)
          : EFFECT.together,
        cta: words.cta,
        blocked: Boolean(g.checks.blocking),
        held: Boolean(g.checks.held),
        heldForOwners: Boolean(g.checks.held),
        copyEditPending,
        ...(review
          ? {
              newSite: {
                filled,
                filledCount: filledCount(review, filled),
                placeholders,
                placeholderCount: placeholderCount(placeholders),
                placeholderNote: placeholderNote(review),
              },
            }
          : {}),
        fix: contentFix(
          check,
          review && /share image/i.test(check.text) ? SITE_IDENTITY : `${editorTo}&fix=1`,
          editorTo
        ),
        // A new site leads with Publish site (design §3.5): a warning's fix (no share
        // image yet) sits beside it, and the CHECKS line keeps saying what's missing.
        fixSecondary: Boolean(review && !g.checks.blocking),
        editorTo,
        approve: async () => {
          const r = await publishTogether(bundleItems(g, waitingList));
          if (r.ok) showInkToast(done, undoFirstPublish([...pages, ...forms]));
          else throw new Error(`${r.message} Open it in the editor.`);
        },
        approveAnyway: async (reason) => {
          const r = await publishTogether(bundleItems(g, waitingList), reason);
          if (r.ok) showInkToast(done, undoFirstPublish([...pages, ...forms]));
          else throw new Error(`${r.message} Open it in the editor.`);
        },
        ask: askContent(all.map((w) => ({ kind: w.kind, id: w.id, workingHash: w.workingHash }))),
        preview: {
          type: 'site',
          // A new site shows its home page, outlined (design §3.1).
          pageId: review ? null : first.id,
          ...(review ? { review: reviewHash(review) } : {}),
          changes: all.map(
            (w) => w.title || (w.kind === 'site' ? 'Site look and navigation' : 'Untitled')
          ),
          changesTitle: words.title,
          where: review ? 'home page, working copy' : `${first.path || '/'} · working copy`,
        },
      };
    };

    const content = waitingList.flatMap((w): ApprovalItem[] => {
      const g = w.group ? groupsById.get(w.group) : undefined;
      if (g) {
        if (shown.has(g.id)) return [];
        shown.add(g.id);
        return [groupItem(g)];
      }
      const title = w.title || (w.kind === 'site' ? 'Site look and navigation' : 'Untitled');
      const approveOne = async (overrideReason?: string) => {
        // A page that still needs its copy edit gets it first, as part of this yes.
        if (w.kind === 'page' && w.copyEditPending) {
          const r = await publishTogether(
            [{ kind: 'page', id: w.id, title, workingHash: w.workingHash, copyEditPending: true }],
            overrideReason
          );
          if (r.ok) showInkToast(`Published “${title}”.`, undoFirstPublish([w]));
          else throw new Error(`${r.message} Open it in the editor.`);
          return;
        }
        const { data: res } = await publish({
          variables: {
            accountId,
            kind: w.kind,
            id: w.kind === 'site' ? undefined : w.id,
            action: 'publish',
            contentHash: w.workingHash,
            overrideReason,
          },
          refetchQueries: contentLists,
        });
        const r = res?.publishTinycmsContent;
        if (r?.ok) showInkToast(`Published “${title}”.`, undoFirstPublish([w]));
        else
          throw new Error(
            `Not published: ${r?.message || r?.refused || 'refused'}. Open it in the editor.`
          );
      };
      const copyEditPending = w.kind === 'page' && Boolean(w.copyEditPending);
      const check = copyEditLine(checkLine(w.checks) || NOTHING_TO_CHECK, copyEditPending);
      const editorTo = reviewHref(w.kind, w.id);
      const fixTo = w.kind === 'page' || w.kind === 'form' ? `${editorTo}&fix=1` : editorTo;
      return [
        {
          key: `${w.kind}:${w.id}`,
          match: [`${w.kind}:${w.id}`],
          tag: CHIP[w.kind][w.live ? 1 : 0],
          who: proposerLine(w.proposedBy, proposer),
          ago: ago(w.updatedAt),
          at: toDate(w.updatedAt)?.getTime() || 0,
          title,
          note: w.note,
          check,
          effect:
            w.kind === 'site' ? EFFECT.site : w.kind === 'form' ? EFFECT.form : EFFECT.publish,
          cta: 'Approve',
          blocked: Boolean(w.checks?.blocking),
          // The main image's description is written right on the card; anything else
          // held goes to the agent.
          held: Boolean(w.checks?.held) && !/main image missing alt text/i.test(check.text),
          heldForOwners: Boolean(w.checks?.held),
          copyEditPending,
          fix:
            w.checks?.held && !/main image missing alt text/i.test(check.text)
              ? { label: 'Ask your agent to fix', to: editorTo }
              : contentFix(check, fixTo, editorTo),
          heroAlt:
            w.kind === 'article' && /main image missing alt text/i.test(check.text)
              ? async (alt: string) => {
                  await updateArticle({
                    variables: { accountId, id: w.id, input: { hero: { alt } } },
                    refetchQueries: ['GetTinycmsArticle'],
                  });
                  showInkToast('Alt text saved.');
                }
              : undefined,
          editorTo,
          approve: () => approveOne(),
          approveAnyway: (reason) => approveOne(reason),
          ask: askContent([{ kind: w.kind, id: w.id, workingHash: w.workingHash }]),
          preview:
            w.kind === 'article'
              ? { type: 'article', articleId: w.id }
              : {
                  type: 'site',
                  pageId: w.kind === 'page' ? w.id : null,
                  changes:
                    w.kind === 'site' && w.note ? w.note.split(' · ') : w.note ? [w.note] : [],
                  changesTitle:
                    w.kind === 'site' ? 'What changes' : w.kind === 'form' ? title : 'This page',
                  where:
                    w.kind === 'page'
                      ? `${w.path || '/'} · working copy`
                      : w.kind === 'form'
                        ? `Form “${title}” · working copy`
                        : 'home page, working copy',
                },
        },
      ];
    });

    // Datasets changed since they went live (only the article's panel showed them).
    const datasets = (data?.datasets || []).map((d: TodayDataset): ApprovalItem => {
      const check = checkLine(d.checks) || NOTHING_TO_CHECK;
      const label = d.name || 'Dataset';
      const article = d.title || 'Untitled';
      const publishIt = async (overrideReason?: string) => {
        const { data: res } = await publish({
          variables: {
            accountId,
            kind: 'dataset',
            id: d.id,
            action: 'publish',
            contentHash: d.workingHash,
            overrideReason,
          },
        });
        const r = res?.publishTinycmsContent;
        if (r?.ok) showInkToast(`Published the ${label} dataset on “${article}”.`);
        else
          throw new Error(
            `Not published: ${r?.message || r?.refused || 'refused'}. Open it in the editor.`
          );
      };
      return {
        key: `dataset:${d.id}`,
        match: [`dataset:${d.id}`],
        tag: d.live ? 'Dataset change' : 'New dataset',
        who: proposer,
        ago: ago(d.updatedAt),
        at: toDate(d.updatedAt)?.getTime() || 0,
        title: `${label} for “${article}”`,
        note:
          d.recordCount != null
            ? `${plural(d.recordCount, 'record')}${d.live ? ', changed since it went live' : ', not published yet'}.`
            : null,
        check,
        effect: EFFECT.dataset,
        cta: 'Publish dataset',
        blocked: Boolean(d.checks?.blocking),
        fix: contentFix(check, reviewHref('dataset', d.id), reviewHref('dataset', d.id)),
        editorTo: reviewHref('dataset', d.id),
        approve: () => publishIt(),
        approveAnyway: (reason) => publishIt(reason),
        ask: askContent([{ kind: 'dataset', id: d.id, workingHash: d.workingHash }]),
        preview: { type: 'dataset', articleId: d.id, name: d.name, records: d.recordCount },
      };
    });

    // An agent's Ask first request: it can't even save a draft until the person says yes.
    const requests = (data?.agentRequests || []).map(
      (r: TodayAgentRequest): ApprovalItem => ({
        key: `agent:${r.id}`,
        match: [`agent:${r.id}`],
        tag:
          r.kind === 'delete'
            ? 'Delete'
            : r.kind === 'surface'
              ? 'Site change'
              : r.kind === 'code'
                ? 'Page code'
                : 'Draft',
        who: `asked by ${r.agentName}`,
        ago: ago(r.requestedAt),
        at: toDate(r.requestedAt)?.getTime() || 0,
        title: `Let ${r.agentName} ${r.action}?`,
        note: `Lapses ${when(new Date(r.expiresAt))} if nobody answers.`,
        check: NOTHING_TO_CHECK,
        effect: EFFECT.agent,
        cta: 'Approve',
        editorTo: '/settings/ai-agents',
        approve: async () => {
          await decideAgent({ variables: { id: r.id, approve: true } });
          showInkToast(`Approved. ${r.agentName} can make this change in the next 15 minutes.`);
        },
        ask: async (note) => {
          await decideAgent({ variables: { id: r.id, approve: false, note: note.trim() || null } });
          return async () => {
            await reopenAgent({ variables: { id: r.id } });
          };
        },
        preview: {
          type: 'request',
          label: `${r.agentName} wants to ${r.action}`,
          quote: r.what,
          where: `Request from ${r.agentName} · nothing changes until you approve`,
        },
      })
    );

    // Technical support asking for access: the owner decides (Settings › Team too).
    const supportView = support.view;
    const supportItems = (supportView?.canDecide ? supportView.pending : []).map(
      (r): ApprovalItem => ({
        key: `support:${r.id}`,
        match: [`support:${r.id}`],
        tag: 'Access',
        who: 'asked by tinycms support',
        ago: ago(r.createdAt),
        at: toDate(r.createdAt)?.getTime() || 0,
        title: `${supportName(r)} from tinycms support is asking for access`,
        note: `${r.ticket ? `Ticket ${r.ticket}. ` : ''}“${r.reason}”`,
        check: NOTHING_TO_CHECK,
        effect: EFFECT.support,
        cta: 'Approve access',
        editorTo: '/settings/team',
        approve: async () => undefined,
        approveFor: async (days) => {
          await decideSupport({ variables: { accountId, id: r.id, approve: true, days } });
          showInkToast(`${supportName(r)} from tinycms support has access until ${endsOn(days)}.`);
          void support.refetch();
        },
        secondary: {
          label: 'Decline',
          run: async () => {
            await decideSupport({ variables: { accountId, id: r.id, approve: false, days: null } });
            showInkToast(`Declined. ${supportName(r)} from tinycms support has no access.`);
            void support.refetch();
          },
        },
        preview: {
          type: 'request',
          label: `${supportName(r)} (${r.requesterEmail})`,
          quote: r.reason,
          lines: [...(r.ticket ? [`Ticket ${r.ticket}`] : []), supportView?.canDo || ''].filter(
            Boolean
          ),
          where: 'Settings › Team · technical support',
        },
      })
    );

    // Undo of Skip / Post now: each post is missed again at its old time.
    const undoFor = (list: ReachPost[]) => ({
      label: 'Undo',
      onClick: () =>
        void Promise.all(
          list.map((p) => {
            const at = toDate(p.scheduleTime);
            return at
              ? restoreMissed({
                  variables: {
                    id: p.id,
                    scheduleTime: at.toISOString(),
                    reviewStatus: p.reviewStatus === 'approved' ? 'approved' : null,
                  },
                })
              : null;
          })
        )
          .then(() => refetch())
          .catch((err) => showInkToast(err instanceof Error ? err.message : 'Couldn’t undo that.')),
    });
    const forEach = async (list: ReachPost[], fn: (p: ReachPost) => Promise<unknown>) => {
      const res = await Promise.allSettled(list.map(fn));
      const bad = res.filter((r): r is PromiseRejectedResult => r.status === 'rejected');
      if (bad.length)
        throw new Error(
          `${list.length - bad.length} of ${list.length} went through. ${
            bad[0].reason instanceof Error ? bad[0].reason.message : 'The rest didn’t.'
          }`
        );
    };
    const staleOf = (list: ReachPost[]) =>
      list.find((x) => staleReason(postText(x), postTime(x))) || null;

    // Post now (or Approve) leads, Skip beside it; a post too late to go out
    // leads with Skip instead and offers no Post now (its fresh post is `fresh`).
    type Act = { label: string; run: () => Promise<void> };
    const leadWith = (stale: string | null, post: Act, skip: Act) =>
      stale
        ? { cta: skip.label, approve: skip.run }
        : { cta: post.label, approve: post.run, secondary: skip };

    // Missed posts on one channel, for one reason, are one item.
    const missed = groupMissed(missedPosts).map((list): ApprovalItem => {
      const p = list[0];
      const channel = channelName(p.platform);
      const undo = undoFor(list);
      // Too late to go out (more than 48 hours, or it names a day that has
      // passed): Skip leads and a fresh post sits beside it (Prototype v16).
      const old = staleOf(list);
      const stale = old ? staleReason(postText(old), postTime(old)) : null;
      const times = list.map(postTime).filter((t): t is Date => Boolean(t));
      const latest = times.map((t) => t.getTime()).sort((a, b) => b - a)[0] || 0;
      const base = {
        match: list.map((x) => `post:${x.id}`),
        tag: POST_STATUS.missed,
        failed: true,
        who: 'needs a decision',
        at: latest,
        check: { tone: 'block' as CheckTone, text: '● Didn’t go out' },
        effect: stale ? staleEffect(stale, list.length) : missedEffect(list.length),
        editorTo: '/reach/channels',
        fresh: old || undefined,
        preview: { type: 'posts' as const, posts: list.map(viewOf), failed: true },
        // Try again would fail until it's reconnected (backlog 8def3fa5).
        ...(!stale && needsReconnect(p)
          ? { fix: { label: `Reconnect ${channel}`, to: '/reach/channels' } }
          : {}),
      };
      if (list.length === 1) {
        const t = postTime(p);
        return {
          ...base,
          key: `missed:${p.id}`,
          ago: t ? ago(t.toISOString()) : '',
          title: `${channel} post didn’t go out`,
          note: missedNote(channel, t, p.failure, ''),
          ...leadWith(
            stale,
            {
              label: retryLabel(t),
              run: async () => {
                await retryPost({ variables: { id: p.id } });
                showInkToast('Posting it now. It goes out in a minute.', undo);
              },
            },
            {
              label: 'Skip',
              run: async () => {
                await skipPost({ variables: { id: p.id } });
                showInkToast('Skipped. It won’t go out.', undo);
              },
            }
          ),
        };
      }
      const w = missedGroupWords(channel, times, p.failure);
      return {
        ...base,
        key: `missed:${list.map((x) => x.id).join(',')}`,
        ago: latest ? ago(new Date(latest).toISOString()) : '',
        title: w.title,
        note: w.note,
        ...leadWith(
          stale,
          {
            label: w.cta,
            run: async () => {
              await forEach(list, (x) => retryPost({ variables: { id: x.id } }));
              showInkToast(w.posted, undo);
            },
          },
          {
            label: w.skip,
            run: async () => {
              await forEach(list, (x) => skipPost({ variables: { id: x.id } }));
              showInkToast(w.skipped, undo);
            },
          }
        ),
      };
    });

    const askCampaign = (c: TodayCampaign) => async (note: string) => {
      const { data: res } = await requestChanges({
        variables: { accountId, kind: 'campaign', id: c.id, note },
      });
      const id = res?.requestTinycmsChanges?.id;
      return async () => {
        if (id) await withdrawChanges({ variables: { accountId, id } });
      };
    };
    const camps = campaigns.map((c: TodayCampaign): ApprovalItem => {
      const editorTo = `/campaigns?campaign=${c.id}`;
      const at = toDate(c.createdAt)?.getTime() || 0;
      if (c.kind === 'always_on') {
        return {
          key: `campaign:${c.id}`,
          match: [`campaign:${c.id}`],
          tag: 'Sharing rule',
          who: proposer,
          ago: ago(c.createdAt),
          at,
          title: c.name,
          note: 'Your agent set this up. Nothing posts until you turn it on.',
          check: NOTHING_TO_CHECK,
          effect: EFFECT.alwaysOn,
          cta: 'Turn it on',
          editorTo,
          approve: async () => {
            await approveCampaign({ variables: { id: c.id } });
            showInkToast(`${c.name} is on.`);
          },
          ask: askCampaign(c),
          preview: {
            type: 'note',
            text: 'Your agent set this sharing rule up. Nothing posts until you turn it on.',
            where: `Social › Automatic · ${c.name}`,
          },
        };
      }
      const waiting = c.posts.filter((p) => p.status === 'in_review');
      const end = calendarDate(c.deadline);
      const dayStart = new Date();
      dayStart.setHours(0, 0, 0, 0);
      const postLines = waiting.map(
        (p) =>
          // A title can be where the key is: a held post's isn't repeated.
          `${p.itemCheck?.holds ? 'A held post' : p.title || 'Untitled post'}${p.scheduledDate ? ` · ${shortDate(calendarDate(p.scheduledDate))}` : ''}${p.voiceCheck?.offVoice ? ` · ● ${offVoiceLine(p.voiceCheck)}` : ''}`
      );
      if (end && end.getTime() < dayStart.getTime()) {
        // Its end date passed and nobody approved it: approving now means nothing.
        const ended = shortDate(end);
        const previous = c.status;
        return {
          key: `campaign:${c.id}`,
          match: [`campaign:${c.id}`],
          tag: 'Social campaign',
          who: proposer,
          ago: ago(c.createdAt || end.toISOString()),
          at,
          title: c.name,
          note: `Ended ${ended} · never approved. Nothing went out.`,
          check: { tone: 'warn', text: `● Ended ${ended} without approval` },
          effect: EFFECT.ended,
          cta: 'Reschedule',
          editorTo,
          prompt: `The “${c.name}” campaign ended ${ended} without being approved, so nothing went out. Suggest new dates, update its posts to match, and send it back to me for approval in tinycms.`,
          approve: async () => undefined,
          // Dismiss: mark it completed — a dated campaign can't be paused. Nothing is deleted.
          secondary: {
            label: 'Dismiss',
            run: async () => {
              await updateProject({ variables: { id: c.id, input: { status: 'completed' } } });
              showInkToast(`${c.name} dismissed. It’s still in Social → Campaigns.`, {
                label: 'Undo',
                onClick: () =>
                  void updateProject({ variables: { id: c.id, input: { status: previous } } }).then(
                    () => refetch()
                  ),
              });
            },
          },
          preview: {
            type: 'note',
            text: `Ended ${ended} without approval.`,
            lines: postLines,
            where: `Campaign · ${c.name}`,
          },
        };
      }
      const range = campaignRange(c);
      const planVoice = cardVoice(
        waiting.map(
          (p): VoiceSubject => ({
            kind: 'plan_post',
            id: p.id,
            network: p.targets[0]?.platform ? channelName(p.targets[0].platform) : undefined,
            check: p.voiceCheck,
          })
        )
      );
      const rewritePlan = () => rewriteInVoice(planVoice.off.map(({ kind, id }) => ({ kind, id })));
      const keyHeldPlan = waiting.find((p) => p.itemCheck?.holds)?.itemCheck;
      const planWarning = waiting.map((p) => itemWarning(p.itemCheck)).find(Boolean);
      return {
        key: `campaign:${c.id}`,
        match: [`campaign:${c.id}`],
        tag: 'Social campaign',
        who: proposer,
        ago: ago(c.createdAt),
        at,
        title: c.name,
        note: `${plural(waiting.length, 'post')} waiting.${range ? ` Runs ${range}.` : ''}`,
        check: keyHeldPlan
          ? { tone: 'block', text: `● ${keyHeldPlan.findings[0].message}` }
          : planVoice.check
            ? { tone: 'warn', text: planVoice.check }
            : planWarning
              ? { tone: 'warn', text: `● ${planWarning}` }
              : NOTHING_TO_CHECK,
        ...(planVoice.off.length ? { voice: { rows: planVoice.rows, rewrite: rewritePlan } } : {}),
        effect: planVoice.effect || EFFECT.campaign,
        cta: planVoice.cta || 'Approve campaign',
        editorTo,
        ...(keyHeldPlan
          ? {
              blocked: true,
              held: true,
              heldForOwners: true,
              heldFix: `In the “${c.name}” campaign, remove the text that looks like a password or key from its held posts, and tell me where it was so I can change it.`,
              fix: { label: 'Ask your agent to fix', to: editorTo },
              approveAnyway: async (reason: string) => {
                const { data: res } = await approveCampaign({ variables: { id: c.id, reason } });
                showInkToast(`Approved. ${res?.approveCampaign?.scheduled || 0} on the calendar.`);
              },
            }
          : {}),
        approve: async () => {
          if (planVoice.allHeld) {
            await rewritePlan();
            return;
          }
          const { data: res } = await approveCampaign({ variables: { id: c.id } });
          const r = res?.approveCampaign;
          showInkToast(
            `Approved. ${r?.scheduled || 0} on the calendar.${
              r?.undated ? ` ${r.undated} need a date before going out.` : ''
            }${
              r?.held - (r?.heldKeys || 0) > 0
                ? ` ${r.held - (r.heldKeys || 0)} held for a style-guide error.`
                : ''
            }${r?.heldKeys ? ` ${r.heldKeys} held for what looks like a password or key.` : ''}`
          );
        },
        ask: askCampaign(c),
        preview: {
          type: 'note',
          text: `${plural(waiting.length, 'post')} waiting for your approval.${range ? ` Runs ${range}.` : ''}`,
          lines: postLines,
          where: `Campaign · ${c.name}`,
        },
      };
    });

    // Waiting posts, one item per channel.
    const byChannel = new Map<string, ReachPost[]>();
    // A held post has its card to itself, so the rest of its channel can go ahead.
    for (const p of waitingPosts.filter((w) => !w.itemCheck?.holds)) {
      const k = (p.platform || '').toLowerCase();
      byChannel.set(k, [...(byChannel.get(k) || []), p]);
    }
    const passedLine = (t: Date, postsNow = true) =>
      `Its time passed (${t.toLocaleDateString([], { month: 'short', day: 'numeric' })}, ${time(t)}).${postsNow ? ' Approving posts it now.' : ''}`;
    const askPosts = (list: ReachPost[]) => async (note: string) => {
      const sent: ReachPost[] = [];
      for (const p of list) {
        await requestPostChanges({ variables: { id: p.id, note } });
        sent.push(p);
      }
      return async () => {
        await Promise.all(
          sent
            .filter((p) => p.proposedTime)
            .map((p) => restoreWaiting({ variables: { id: p.id, proposedTime: p.proposedTime } }))
        );
      };
    };
    const heldCards = waitingPosts
      .filter((p) => p.itemCheck?.holds)
      .map((p): ApprovalItem => {
        const updated = toDate(p.updatedAt);
        return {
          key: `post-held:${p.id}`,
          match: [`post:${p.id}`],
          tag: 'Social posts',
          who: proposer,
          ago: updated ? ago(updated.toISOString()) : '',
          at: updated?.getTime() || 0,
          title: `Post for ${channelName(p.platform)}`,
          note: 'Waits for a fix. It won’t go out on its own.',
          check: { tone: 'block', text: `● ${p.itemCheck!.findings[0].message}` },
          effect:
            'Approving it anyway posts it as it is, and your reason goes in the Activity log.',
          cta: 'Approve',
          blocked: true,
          held: true,
          heldForOwners: true,
          heldFix: `On social post ${p.id}, remove the text that looks like a password or key, and tell me where it was so I can change it.`,
          fix: { label: 'Ask your agent to fix', to: '/reach/calendar' },
          approve: async () => undefined,
          approveAnyway: async (reason) => {
            await approvePost({ variables: { id: p.id, reason } });
            showInkToast('Approved. Your reason is in the Activity log.');
          },
          editorTo: '/reach/calendar',
          ask: askPosts([p]),
          preview: { type: 'posts', posts: [viewOf(p)] },
        };
      });
    const postCards = [...byChannel].map(([platform, list]): ApprovalItem => {
      const now = Date.now();
      const times = list.map(postTime).filter(Boolean) as Date[];
      const sorted = [...times].sort((a, b) => a.getTime() - b.getTime());
      const first = sorted[0];
      const last = sorted[sorted.length - 1];
      const daysOut = first && last ? Math.ceil((last.getTime() - now) / DAY) : 0;
      const passed = list.filter((p) => {
        const t = postTime(p);
        return Boolean(t && t.getTime() < now);
      });
      const single = list.length === 1 && passed.length === 1 ? postTime(list[0]) : null;
      // Every post's time passed and each is too late to go out: Skip leads,
      // with a fresh post beside it, as for a post that didn't go out.
      const staleOne =
        passed.length === list.length ? list.map((p) => staleReason(postText(p), postTime(p))) : [];
      const stale = staleOne.length && staleOne.every(Boolean) ? staleOne[0] : null;
      const channel = channelName(platform);
      const skipAll = async () => {
        for (const p of list) await skipPost({ variables: { id: p.id } });
        showInkToast(
          list.length === 1
            ? 'Skipped. This post won’t go out.'
            : `Skipped ${list.length}. They won’t go out.`
        );
      };
      const updated = list.map((p) => toDate(p.updatedAt)?.getTime() || 0).sort((a, b) => b - a)[0];
      const voice = cardVoice(
        list.map(
          (p): VoiceSubject => ({ kind: 'post', id: p.id, network: channel, check: p.voiceCheck })
        )
      );
      const rewriteOff = () => rewriteInVoice(voice.off.map(({ kind, id }) => ({ kind, id })));
      const clearPosts = list.filter((p) => !p.voiceCheck?.holds);
      const warning = list.map((p) => itemWarning(p.itemCheck)).find(Boolean);
      return {
        key: `posts:${platform}`,
        match: list.map((p) => `post:${p.id}`),
        tag: 'Social posts',
        who: proposer,
        ago: updated ? ago(new Date(updated).toISOString()) : '',
        at: updated || 0,
        title: plural(list.length, `post for ${channel}`, `posts for ${channel}`),
        note: !first
          ? null
          : stale
            ? single
              ? passedLine(single, false)
              : `All ${list.length} passed their time.`
            : single
              ? passedLine(single)
              : passed.length
                ? `${passed.length} passed ${passed.length === 1 ? 'its' : 'their'} time; approving posts ${passed.length === 1 ? 'it' : 'them'} now.`
                : list.length === 1
                  ? `Goes out ${when(first)}.`
                  : daysOut > 1
                    ? `Scheduled over the next ${daysOut} days.`
                    : `Scheduled from ${when(first)}.`,
        check: voice.check
          ? { tone: 'warn', text: voice.check }
          : warning
            ? { tone: 'warn', text: `● ${warning}` }
            : NOTHING_TO_CHECK,
        ...(voice.off.length ? { voice: { rows: voice.rows, rewrite: rewriteOff } } : {}),
        effect: stale
          ? staleEffect(stale, list.length)
          : voice.effect || postsEffect(list.length, passed.length),
        editorTo: '/reach/calendar',
        ...(stale
          ? { cta: list.length > 1 ? 'Skip all' : 'Skip', approve: skipAll, fresh: list[0] }
          : {
              cta: voice.cta || (list.length > 1 ? `Approve all ${list.length}` : 'Approve'),
              extra: single ? { label: 'Skip', run: skipAll } : undefined,
              approve: async () => {
                if (voice.allHeld) {
                  await rewriteOff();
                  return;
                }
                for (const p of clearPosts) await approvePost({ variables: { id: p.id } });
                showInkToast(
                  `Approved ${plural(clearPosts.length, 'post')} for ${channel}.${
                    voice.held.length ? ` ${voice.held.length} held for a style-guide error.` : ''
                  }`
                );
              },
            }),
        ask: askPosts(list),
        preview: {
          type: 'posts',
          posts: list.map((p) => {
            const v = viewOf(p);
            const t = postTime(p);
            return t && t.getTime() < now ? { ...v, warn: [...v.warn, passedLine(t)] } : v;
          }),
        },
      };
    });

    // A draft campaign (set up, never scheduled) starting within a week: it
    // won't start on its own. Not an approval, so it isn't counted.
    const waitingIds = new Set(campaigns.map((c) => c.id));
    const today0 = new Date();
    today0.setHours(0, 0, 0, 0);
    const soonDrafts = allCampaigns
      .filter((c) => c.kind === 'campaign' && c.status === 'planning' && !waitingIds.has(c.id))
      .map((c) => ({ c, start: campaignStart(c) }))
      .filter(({ start }) => {
        if (!start) return false;
        const days = Math.round((start.getTime() - today0.getTime()) / DAY);
        return days >= 0 && days <= 7;
      })
      .map(({ c, start }): ApprovalItem => {
        const days = Math.round((start!.getTime() - today0.getTime()) / DAY);
        const starts =
          days === 0
            ? 'Starts today'
            : days === 1
              ? 'Starts tomorrow'
              : `Starts ${shortDate(start)}`;
        return {
          key: `draft:${c.id}`,
          match: [`campaign:${c.id}`],
          tag: 'Social campaign',
          who: 'set up, not scheduled',
          ago: starts.replace('Starts ', ''),
          at: 0,
          title: c.name,
          note: `${starts} · not scheduled yet. Nothing goes out until you schedule it.`,
          check: { tone: 'warn', text: `● ${starts}, not scheduled` },
          effect: EFFECT.draft,
          cta: 'Schedule campaign',
          editorTo: `/campaigns?campaign=${c.id}`,
          notApproval: true,
          approve: async () => {
            await approveCampaign({ variables: { id: c.id } });
            showInkToast(`${c.name} is scheduled. It starts ${shortDate(start)}.`);
          },
          preview: {
            type: 'note',
            text: `${starts} · not scheduled yet. Nothing goes out until you schedule it.`,
            where: `Campaign · ${c.name}`,
          },
        };
      });

    // By group, oldest first within each (design review round 27, §6).
    return orderQueue([
      ...content,
      ...datasets,
      ...requests,
      ...supportItems,
      ...missed,
      ...camps,
      ...heldCards,
      ...postCards,
      ...soonDrafts,
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    data,
    campaigns,
    allCampaigns,
    waitingPosts,
    missedPosts,
    accountId,
    accountNames,
    agentConnected,
    support.view,
    starterReview,
    siteHost,
  ]);

  const count = items.filter((i) => !i.notApproval).length;
  const refetchAll = () =>
    Promise.all([refetch(), support.refetch(), ...(newSiteWaiting ? [starterQ.refetch()] : [])]);
  return {
    items,
    count,
    loading: today.loading || today.reachLoading,
    siteName,
    refetch: refetchAll,
  };
}
